import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Calendar, Package, Sparkles, Layers, Gamepad2, BookOpen, Bell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import api from '@/lib/api';

export default function Home() {
  const [extensions, setExtensions] = useState([]);

  useEffect(() => {
    api.get('/extensions?status=ouvert').then(r => setExtensions(r.data)).catch(console.error);
  }, []);

  const formatDate = (d) => new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div className="min-h-screen">
      <section className="relative overflow-hidden bg-gradient-to-br from-violet-50 via-white to-amber-50/50">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl py-20 md:py-32">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}>
              <span className="font-accent text-2xl text-amber-500" data-testid="hero-accent">Votre librairie ludique</span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mt-3 text-slate-900">
                Précommandez vos{' '}
                <span className="text-violet-600">cartes</span> et{' '}
                <span className="text-violet-600">jeux</span> préférés
              </h1>
              <p className="text-base md:text-lg text-slate-600 mt-6 leading-relaxed max-w-lg">
                Réservez dès maintenant les dernières extensions TCG, jeux de société et bien plus encore chez Plume & Pion.
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link to="/catalogue">
                  <Button className="rounded-full px-8 py-3 text-base shadow-lg shadow-violet-600/20 hover:shadow-violet-600/30 transition-all" data-testid="hero-cta-btn">
                    Voir les précommandes <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.2 }} className="hidden lg:block">
              <div className="relative">
                <div className="absolute -inset-4 bg-violet-200/30 rounded-3xl blur-2xl" />
                <img
                  src="https://images.unsplash.com/photo-1769288361820-898812b71482?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxib2FyZCUyMGdhbWUlMjBzdG9yZSUyMGludGVyaW9yJTIwc2hlbHZlc3xlbnwwfHx8fDE3NzAzMzE3OTl8MA&ixlib=rb-4.1.0&q=85"
                  alt="Plume et Pion boutique"
                  className="relative rounded-2xl shadow-2xl object-cover h-[420px] w-full"
                />
                <div className="absolute -bottom-4 -left-4 bg-white rounded-xl shadow-lg p-4 border border-slate-100">
                  <div className="flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-amber-500" />
                    <span className="font-accent text-lg text-slate-700">Nouveautés chaque semaine !</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
        <div className="text-center mb-10">
          <h2 className="text-3xl font-bold tracking-tight text-slate-900">Nos univers</h2>
          <p className="text-slate-500 mt-2">Explorez nos trois mondes</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <Link to="/catalogue?univers=tcg" className="block bg-violet-600 rounded-2xl p-8 hover:bg-violet-700 transition-all hover:scale-[1.02] group" data-testid="univers-tcg">
              <Layers className="h-10 w-10 text-violet-200 mb-4" />
              <h3 className="text-2xl font-bold text-white">TCG</h3>
              <p className="text-violet-200 text-sm mt-2">Pokémon, Magic, Lorcana, One Piece, Naruto...</p>
              <span className="inline-flex items-center gap-1 text-white/80 text-sm mt-4 group-hover:text-white transition-colors">Voir les extensions <ArrowRight className="h-3.5 w-3.5" /></span>
            </Link>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <Link to="/catalogue?univers=jeu_de_societe" className="block bg-emerald-600 rounded-2xl p-8 hover:bg-emerald-700 transition-all hover:scale-[1.02] group" data-testid="univers-jeux">
              <Gamepad2 className="h-10 w-10 text-emerald-200 mb-4" />
              <h3 className="text-2xl font-bold text-white">Jeux de société</h3>
              <p className="text-emerald-200 text-sm mt-2">Les jeux les plus attendus du moment</p>
              <span className="inline-flex items-center gap-1 text-white/80 text-sm mt-4 group-hover:text-white transition-colors">Voir les jeux <ArrowRight className="h-3.5 w-3.5" /></span>
            </Link>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
            <Link to="/catalogue?univers=bd_manga" className="block bg-rose-600 rounded-2xl p-8 hover:bg-rose-700 transition-all hover:scale-[1.02] group" data-testid="univers-bd-manga">
              <BookOpen className="h-10 w-10 text-rose-200 mb-4" />
              <h3 className="text-2xl font-bold text-white">BD & Manga</h3>
              <p className="text-rose-200 text-sm mt-2">Suivi de séries, nouvelles sorties et précommandes</p>
              <span className="inline-flex items-center gap-1 text-white/80 text-sm mt-4 group-hover:text-white transition-colors">Découvrir <ArrowRight className="h-3.5 w-3.5" /></span>
            </Link>
          </motion.div>
        </div>
      </section>

      <section className="py-8 container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="bg-slate-900 rounded-3xl p-8 md:p-12 grid lg:grid-cols-12 gap-8 items-center" data-testid="series-cta">
          <div className="lg:col-span-8">
            <span className="font-accent text-2xl text-amber-400">Lecteurs de BD & Manga</span>
            <h2 className="text-3xl font-bold tracking-tight text-white mt-2">Dites-nous quelles séries vous suivez</h2>
            <p className="text-slate-300 mt-3 max-w-2xl">Indiquez vos séries préférées : dès qu'un nouveau tome sort, nous vous prévenons par email et vous pouvez le réserver avant tout le monde.</p>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end">
            <Link to="/series">
              <Button className="rounded-full px-8 py-3 text-base bg-rose-600 hover:bg-rose-700 shadow-lg shadow-rose-600/30" data-testid="series-cta-btn">
                <Bell className="mr-2 h-4 w-4" /> Suivre mes séries
              </Button>
            </Link>
          </div>
        </motion.div>
      </section>

      <section className="py-20 md:py-28 container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
        <div className="flex items-end justify-between mb-10">
          <div>
            <h2 className="text-3xl font-bold tracking-tight text-slate-900">Précommandes ouvertes</h2>
            <p className="text-slate-500 mt-2">Réservez avant la date limite</p>
          </div>
          {extensions.length > 6 && (
            <Link to="/catalogue">
              <Button variant="ghost" className="text-violet-600 hover:text-violet-700 rounded-full" data-testid="see-all-btn">
                Tout voir <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </Link>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {extensions.slice(0, 6).map((ext, i) => (
            <motion.div key={ext.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08, duration: 0.4 }}>
              <Link to={`/extension/${ext.id}`} className="block bg-white border border-slate-200 rounded-2xl overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all duration-300 group" data-testid={`extension-card-${ext.id}`}>
                {ext.image_url ? (
                  <div className="h-44 overflow-hidden">
                    <img src={ext.image_url} alt={ext.name} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                ) : (
                  <div className="h-44 bg-gradient-to-br from-violet-50 to-slate-50 flex items-center justify-center">
                    {ext.product_type === 'tcg' && <Layers className="h-12 w-12 text-violet-300" />}
                    {ext.product_type === 'jeu_de_societe' && <Gamepad2 className="h-12 w-12 text-emerald-300" />}
                    {ext.product_type === 'bd_manga' && <BookOpen className="h-12 w-12 text-rose-300" />}
                    {!ext.product_type && <Package className="h-12 w-12 text-slate-300" />}
                  </div>
                )}
                <div className="p-5">
                  {ext.product_type === 'tcg' && ext.tcg_name && <Badge className="bg-violet-100 text-violet-700 border-violet-200 mb-2 text-xs">{ext.tcg_name}</Badge>}
                  {ext.product_type === 'jeu_de_societe' && <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 mb-2 text-xs">Jeu de société</Badge>}
                  {ext.product_type === 'bd_manga' && <Badge className="bg-rose-100 text-rose-700 border-rose-200 mb-2 text-xs">BD & Manga</Badge>}
                  <h3 className="text-lg font-semibold text-slate-900 group-hover:text-violet-600 transition-colors">{ext.name}</h3>
                  <div className="flex items-center gap-2 mt-3 text-sm text-slate-500">
                    <Calendar className="h-3.5 w-3.5" />
                    <span>Sortie : {formatDate(ext.release_date)}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-1 text-sm text-slate-500">
                    <Package className="h-3.5 w-3.5" />
                    <span>{ext.items?.length || 0} produit{(ext.items?.length || 0) > 1 ? 's' : ''}</span>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
        {extensions.length === 0 && (
          <div className="text-center py-20">
            <Package className="h-16 w-16 mx-auto text-slate-200" />
            <p className="mt-4 text-slate-500 text-lg">Aucune précommande ouverte pour le moment.</p>
            <p className="text-slate-400 text-sm mt-1">Revenez bientôt !</p>
          </div>
        )}
      </section>
    </div>
  );
}
